'use client';

import { useRef, useState } from 'react';
import type { ZipCityLocation } from '@/components/ZipCity';
import { useRouter } from '@/i18n/navigation';

/**
 * `initialQuery` seeds the input, so the search results page can show the term
 * the visitor searched for rather than an empty box. Typing owns the field from
 * then on — until the seed itself changes.
 *
 * It has to re-seed, because the finder's own submit navigates to the route it
 * already sits on: `/standorte/suche/?locationSearchTerm=…` is a same-route
 * navigation, so Next re-renders the server component and keeps this client
 * tree mounted. Without the re-seed the input would keep whatever it held
 * before — most visibly after a Back, where the results are one term and the
 * box shows another.
 */
export function useLocationFinder(anchor?: string, initialQuery = '') {
  const router = useRouter();
  const [query, setQuery] = useState(initialQuery);
  // React's documented "adjust state when a prop changes" pattern: re-render
  // during the same pass, rather than an effect that paints the stale term
  // first.
  const [seed, setSeed] = useState(initialQuery);
  if (initialQuery !== seed) {
    setSeed(initialQuery);
    setQuery(initialQuery);
  }
  const [selectedSlug, setSelectedSlug] = useState<string | undefined>(
    undefined,
  );
  // Tagged with the query it was captured for: the cache must never answer a
  // different term with a leftover list from an earlier, unrelated search —
  // the name-search branch happens to be protected from that by its own
  // prefix filter, but the PLZ branch uses every cached entry unfiltered, so
  // an untagged cache could redirect a "73430" submit to a Standort that only
  // ever matched an earlier "Berlin" search.
  const suggestionsRef = useRef<{ query: string; items: ZipCityLocation[] }>({
    query: '',
    items: [],
  });
  const suffix = anchor ? `#${anchor}` : '';

  /**
   * The anchor names a section on the **Standort page** the finder sends the
   * visitor to (e.g. `#beratungstermin`, the Book Appointment widget), so it
   * only applies when we actually land on one.
   */
  const navigateToLocation = (slug: string) =>
    router.push(`/nachhilfe/${slug}/${suffix}`);

  /**
   * The search-results page has no such section, so it never carries the
   * anchor — appending it produced a dead fragment on a URL that already has a
   * query string (`/standorte/suche/?locationSearchTerm=Be#preise`).
   */
  const navigateToSearch = (path: string) => router.push(path);

  const handleValueChange = (value: string) => {
    setQuery(value);
    setSelectedSlug(undefined);
  };

  const handleSelect = (loc: ZipCityLocation) => {
    setSelectedSlug(loc.slug);
    navigateToLocation(loc.slug);
  };

  const handleSubmit = (q: string, results: ZipCityLocation[]) => {
    const trimmed = q.trim();
    if (results.length > 0) {
      suggestionsRef.current = { query: trimmed, items: results };
    }
    const cached =
      suggestionsRef.current.query === trimmed
        ? suggestionsRef.current.items
        : [];
    const candidates = results.length > 0 ? results : cached;

    if (selectedSlug) {
      navigateToLocation(selectedSlug);
      return;
    }

    // SH-555: a PLZ of any length (2–5 digits) redirects straight to the
    // Standort page when it resolves to exactly one match — whether that's a
    // direct hit or the backend's own truncating-prefix fallback. `candidates`
    // already reflects the backend's full decision tree (the autocomplete
    // calls the same endpoint), just capped at its own limit — but the cap
    // can never turn a true count of 2+ into an observed "1", so it never
    // misleads this specific check.
    const matches = /^\d+$/.test(trimmed)
      ? candidates
      : candidates.filter((r) =>
          r.name.toLowerCase().startsWith(trimmed.toLowerCase()),
        );

    if (matches.length === 1) {
      navigateToLocation(matches[0]!.slug);
    } else if (trimmed) {
      navigateToSearch(
        `/standorte/suche/?locationSearchTerm=${encodeURIComponent(trimmed)}`,
      );
    } else {
      navigateToSearch('/standorte/suche/');
    }
  };

  return { query, handleValueChange, handleSelect, handleSubmit };
}
